import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, UserCircle, Trash2, AlertTriangle, HelpCircle, Info } from 'lucide-react';
import axios from 'axios';

// Stabiles innerHTML: React 19 setzt sonst bei jedem Re-Render innerHTML neu (Videos/Iframes stoppen)
export const SafeHtml = React.memo(({ html, ...props }) => {
  const inner = useMemo(() => ({ __html: html }), [html]);
  return <div {...props} dangerouslySetInnerHTML={inner} />;
});

const formatMB = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

// ===== Zentraler App-Dialog (ersetzt window.confirm / alert / prompt) =====
let _openDialog = null;

const openDialog = (cfg) => new Promise((resolve) => {
  if (!_openDialog) {
    if (cfg.type === 'confirm') return resolve(window.confirm(cfg.message));
    if (cfg.type === 'prompt') return resolve(window.prompt(cfg.message, cfg.defaultValue));
    window.alert(cfg.message); return resolve(undefined);
  }
  _openDialog({ ...cfg, resolve });
});

export const confirmDialog = (message, opts = {}) => openDialog({
  type: 'confirm', message, title: opts.title || 'Bist du sicher?',
  confirmLabel: opts.confirmLabel || 'Löschen', cancelLabel: opts.cancelLabel || 'Abbrechen', danger: opts.danger !== false,
});
export const alertDialog = (message, opts = {}) => openDialog({
  type: 'alert', message, title: opts.title || 'Hinweis', confirmLabel: opts.confirmLabel || 'OK', danger: !!opts.danger,
});
export const promptDialog = (message, opts = {}) => openDialog({
  type: 'prompt', message, title: opts.title || 'Eingabe', defaultValue: opts.defaultValue ?? '', placeholder: opts.placeholder || '',
  inputType: opts.inputType || 'text', confirmLabel: opts.confirmLabel || 'Übernehmen', cancelLabel: 'Abbrechen', danger: false,
});

// Auswahl im WYSIWYG-Editor ueber einen Dialog hinweg erhalten
export const saveSelection = () => { const sel = window.getSelection(); return sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null; };
export const restoreSelection = (range) => {
  if (!range) return;
  const node = range.commonAncestorContainer;
  const editor = (node.nodeType === 1 ? node : node.parentElement)?.closest?.('[contenteditable]');
  if (editor) editor.focus();
  const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range);
};

export function DialogHost() {
  const [dlg, setDlg] = useState(null);
  const [value, setValue] = useState('');
  useEffect(() => {
    _openDialog = (cfg) => { setValue(cfg.defaultValue ?? ''); setDlg(cfg); };
    return () => { _openDialog = null; };
  }, []);
  const close = (result) => { dlg.resolve(result); setDlg(null); };
  const cancel = () => close(dlg.type === 'confirm' ? false : dlg.type === 'prompt' ? null : undefined);
  const ok = () => close(dlg.type === 'confirm' ? true : dlg.type === 'prompt' ? value : undefined);
  useEffect(() => {
    if (!dlg) return;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); cancel(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });
  if (!dlg) return null;
  const Icon = dlg.type === 'alert' ? (dlg.danger ? AlertTriangle : Info) : dlg.type === 'prompt' ? HelpCircle : (dlg.danger ? Trash2 : HelpCircle);
  return (
    <div className="modal-overlay app-dialog-overlay" onClick={cancel} data-testid="app-dialog">
      <div className={`app-dialog ${dlg.danger ? 'is-danger' : ''}`} role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
        <div className="app-dialog-icon"><Icon size={22} /></div>
        <h3 className="app-dialog-title" data-testid="app-dialog-title">{dlg.title}</h3>
        <p className="app-dialog-message" data-testid="app-dialog-message">{dlg.message}</p>
        {dlg.type === 'prompt' && (
          <input autoFocus type={dlg.inputType} className="app-dialog-input" value={value} placeholder={dlg.placeholder}
            onChange={e => setValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); ok(); } }} data-testid="app-dialog-input" />
        )}
        <div className="app-dialog-actions">
          {dlg.type !== 'alert' && <button type="button" className="app-dialog-btn cancel" onClick={cancel} data-testid="app-dialog-cancel">{dlg.cancelLabel}</button>}
          <button type="button" autoFocus={dlg.type !== 'prompt'} className={`app-dialog-btn ${dlg.danger ? 'danger' : 'primary'}`} onClick={ok} data-testid="app-dialog-confirm">{dlg.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

const STAGE_LABELS = { queued: 'Warteschlange…', assembling: 'Datei wird zusammengesetzt…', transcoding: 'Wird auf 720p konvertiert…', uploading: 'Wird gespeichert…' };

// Chunked upload for large files (bypasses proxy limits) with progress callback
export async function uploadFileChunked(file, API_URL, authHeaders, onProgress) {
  const CHUNK_SIZE = 4 * 1024 * 1024;
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  const rawId = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`;
  const uploadId = rawId.replace(/[^a-f0-9]/gi, '').toLowerCase().padEnd(16, '0').slice(0, 32);
  // Fortschritt sofort anzeigen + nach jedem Chunk aktualisieren (Fallback, falls onUploadProgress nicht feuert, z.B. Service Worker / WebView)
  if (onProgress) onProgress({ name: file.name, loaded: 0, total: file.size, finalizing: false });
  for (let i = 0; i < totalChunks; i++) {
    const blob = file.slice(i * CHUNK_SIZE, Math.min(file.size, (i + 1) * CHUNK_SIZE));
    const fd = new FormData();
    fd.append('upload_id', uploadId);
    fd.append('chunk_index', String(i));
    fd.append('total_chunks', String(totalChunks));
    fd.append('file', blob, file.name);
    await axios.post(`${API_URL}/api/community/uploads/chunk`, fd, {
      headers: { ...authHeaders },
      onUploadProgress: (ev) => {
        const loaded = Math.min(i * CHUNK_SIZE + ev.loaded, file.size);
        if (onProgress) onProgress({ name: file.name, loaded, total: file.size, finalizing: false });
      },
    });
    if (onProgress) onProgress({ name: file.name, loaded: Math.min((i + 1) * CHUNK_SIZE, file.size), total: file.size, finalizing: false });
  }
  if (onProgress) onProgress({ name: file.name, loaded: file.size, total: file.size, finalizing: true });
  const resp = await axios.post(`${API_URL}/api/community/uploads/complete`,
    { upload_id: uploadId, filename: file.name, total_chunks: totalChunks },
    { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
  const result = resp.data;
  if (result.processing && result.job_id) {
    // Video wird serverseitig verarbeitet (720p-Konvertierung) - Status pollen, kurze Netzwerkaussetzer tolerieren
    const deadline = Date.now() + 60 * 60 * 1000;
    let failures = 0;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 3000));
      let js;
      try {
        js = await axios.get(`${API_URL}/api/community/uploads/job/${result.job_id}`, { headers: { ...authHeaders } });
        failures = 0;
      } catch (pollErr) {
        if (pollErr.response?.status === 404 || ++failures >= 6) throw pollErr;
        continue;
      }
      if (js.data.status === 'done') return { file_url: js.data.file_url, file_name: js.data.file_name };
      if (js.data.status === 'error') {
        const msg = js.data.detail || 'Videoverarbeitung fehlgeschlagen. Bitte erneut versuchen.';
        const err = new Error(msg); err.response = { data: { detail: msg } }; throw err;
      }
      if (onProgress) onProgress({ name: file.name, loaded: file.size, total: file.size, finalizing: true, stage: js.data.stage, stagePct: js.data.progress });
    }
    const msg = 'Zeitüberschreitung bei der Videoverarbeitung. Bitte erneut versuchen.';
    const err = new Error(msg); err.response = { data: { detail: msg } }; throw err;
  }
  return result;
}

export const UploadProgress = ({ progress }) => {
  if (!progress) return null;
  const uploadPct = progress.total ? Math.min(100, Math.round((progress.loaded / progress.total) * 100)) : 0;
  const stageLabel = progress.stage ? STAGE_LABELS[progress.stage] || 'Wird verarbeitet…' : 'Wird verarbeitet…';
  const stagePct = typeof progress.stagePct === 'number' ? progress.stagePct : null;
  const barPct = progress.finalizing ? (stagePct ?? 100) : uploadPct;
  return (
    <div className={`upload-progress ${progress.finalizing ? 'is-processing' : ''}`} data-testid="upload-progress">
      <div className="upload-progress-info">
        <span className="upload-progress-name">{progress.name}</span>
        <span className="upload-progress-pct" data-testid="upload-progress-pct">
          {progress.finalizing ? `${stageLabel}${stagePct !== null && progress.stage && progress.stage !== 'queued' ? ` ${stagePct}%` : ''}` : `${uploadPct}% · ${formatMB(progress.loaded)} / ${formatMB(progress.total)} MB`}
        </span>
      </div>
      <div className="upload-progress-track"><div className={`upload-progress-fill ${progress.finalizing && stagePct === null ? 'is-indeterminate' : ''}`} style={{ width: `${barPct}%` }} /></div>
    </div>
  );
};

const ALLOWED_IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export const convertImageToJpeg = (file, maxDim = 1200) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const s = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * s);
        height = Math.round(height * s);
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (b) => (b ? resolve(new File([b], (file.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' })) : reject(new Error('convert'))),
        'image/jpeg', 0.9
      );
    } catch (err) { URL.revokeObjectURL(url); reject(err); }
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('load')); };
  img.src = url;
});

// Normalizes any picked image: converts HEIC/unknown formats to JPEG (Safari decodes HEIC natively),
// downscales oversized photos. Throws Error with a German message when not usable.
export const prepareImageFile = async (file, maxMB = 5) => {
  if (!file) throw new Error('Keine Datei ausgewählt');
  let out = file;
  const isHeic = /heic|heif/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '');
  if (isHeic || !ALLOWED_IMG_TYPES.includes(file.type)) {
    try {
      out = await convertImageToJpeg(file);
    } catch (err) {
      throw new Error('Dieses Bildformat wird nicht unterstützt. Bitte wähle ein Foto im JPEG- oder PNG-Format.');
    }
  }
  if (out.size > maxMB * 1024 * 1024 && out.type !== 'image/gif') {
    try { out = await convertImageToJpeg(out); } catch (err) { /* keep original */ }
  }
  if (out.size > maxMB * 1024 * 1024) {
    throw new Error(`Bild darf maximal ${maxMB} MB groß sein`);
  }
  return out;
};

export const EMOJI_CATS = {
  'Smileys': ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😉','😊','😇','🥰','😍','🤩','😘','😗','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🫡','😐','😑','😶','🫥','😏','😒','🙄','😬','😮‍💨','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🥴','😵','🤯','🥳','🥸','😎','🤓','🧐'],
  'Gesten': ['👍','👎','👏','🙌','🤝','🫶','💪','✌️','🤞','🤟','🤘','🤙','👋','🖐️','✋','👌','🫰','👊','✊','🫵','☝️','👆','👇','👈','👉'],
  'Herzen': ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❤️‍🔥','❤️‍🩹','💕','💞','💓','💗','💖','💘','💝'],
  'Feiern': ['🎉','🎊','🎈','🎁','🎀','🎗️','🏆','🥇','🥈','🥉','🏅','🎖️','🎯','🪩','✨','🌟','💫','⭐','🔥','💥','💢','💣'],
  'Objekte': ['💰','💵','💴','💶','💷','💎','📢','📣','🔔','📱','💻','🖥️','📧','📬','📝','📋','📊','📈','📉','🔑','🗝️','🔒','🔓','🛡️'],
  'Symbole': ['✅','❌','⭕','🚫','💯','⚡','☀️','🌙','⏰','⏳','🔴','🟢','🔵','🟡','🟠','🟣','⬛','⬜','▶️','⏸️','⏹️','🔄','➡️','⬅️','⬆️','⬇️'],
  'Natur': ['🌍','🌎','🌏','🌈','☁️','⛈️','❄️','🌊','🌸','🌺','🌻','🌹','🍀','🌿','🍁','🌴','🌳','🐶','🐱','🦁','🐻','🦊','🐸','🦋','🐝'],
  'Essen': ['🍕','🍔','🍟','🌭','🌮','🍣','🍱','🥗','🍰','🎂','🍩','🍪','☕','🍵','🥤','🍺','🍻','🥂','🍷','🧃'],
};

export function Modal({ show, onClose, title, children, preventBackdropClose }) {
  if (!show) return null;
  return (
    <div className="modal-overlay" onClick={preventBackdropClose ? undefined : onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="search-bar" data-testid="search-bar">
      <Search size={16} className="search-icon" />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || 'Suchen...'} className="search-input" data-testid="search-input" />
      {value && <button className="search-clear" onClick={() => onChange('')}><X size={14} /></button>}
    </div>
  );
}

export function matchesSearch(item, term, fields) {
  if (!term) return true;
  const lower = term.toLowerCase();
  const matchesFields = fields.some(f => {
    const val = typeof f === 'function' ? f(item) : item[f];
    return val && String(val).toLowerCase().includes(lower);
  });
  if (matchesFields) return true;
  if (item.project_ids) {
    return Object.values(item.project_ids).some(v => v && String(v).toLowerCase().includes(lower));
  }
  return false;
}

export function ProjectInput({ project, initialValue, apiUrl, authHeaders, onSaved, targetUserId }) {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (value === initialValue) return;
    setSaving(true);
    try {
      const body = { project_id: project.id, user_project_id: value };
      if (targetUserId) body.target_user_id = targetUserId;
      const resp = await axios.put(`${apiUrl}/api/community/profile/project`, body, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      if (resp.data.user && onSaved) onSaved(resp.data.user);
    } catch (err) {
      console.error('Project save error:', err);
    }
    setSaving(false);
  };

  return (
    <div className="project-field">
      <label className="project-label">{project.name}</label>
      <input
        className="project-input"
        value={value}
        placeholder={`Deine ${project.name} User-ID`}
        onChange={e => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); }}}
        data-testid={`project-${project.id}`}
      />
    </div>
  );
}

export const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
};

export const formatDateShort = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return d; }
};

export const getRoleBadgeClass = (r) => ({ admin: 'badge-admin', coach: 'badge-coach', sponsor: 'badge-sponsor' }[r] || 'badge-user');
export const getRoleLabel = (r) => ({ admin: 'Botschafter', coach: 'Botschafter', sponsor: 'Förderer', user: 'Freigeist' }[r] || r);
export const getRoleLabelAdmin = (r) => ({ admin: 'Admin-Botschafter', coach: 'Botschafter', sponsor: 'Förderer', user: 'Freigeist' }[r] || r);
export const getStatusBadgeClass = (s) => ({ active: 'badge-active', pending: 'badge-pending', email_pending: 'badge-email-pending', rejected: 'badge-rejected', banned: 'badge-banned' }[s] || '');
export const getStatusLabel = (s) => ({ active: 'Aktiv', pending: 'Ausstehend', email_pending: 'E-Mail-Bestätigung ausstehend', rejected: 'Abgelehnt', banned: 'Gesperrt' }[s] || s);
export const getActionLabel = (a) => ({
  registration: 'Registrierung', email_verified: 'E-Mail bestätigt', approved: 'Genehmigt', rejected: 'Abgelehnt', rejected_and_deleted: 'Abgelehnt & gelöscht',
  admin_seeded: 'Admin erstellt', role_changed: 'Rolle geändert', user_updated: 'Profil geändert (Admin)', user_deleted: 'User gelöscht',
  password_reset: 'Passwort Reset', password_changed: 'Passwort geändert', email_changed: 'E-Mail geändert', profile_self_updated: 'Profil geändert',
  course_created: 'Projekt erstellt', course_updated: 'Projekt geändert', course_deleted: 'Projekt gelöscht',
  lesson_created: 'Lektion erstellt', lesson_deleted: 'Lektion gelöscht',
  event_created: 'Termin erstellt', event_updated: 'Termin geändert', event_deleted: 'Termin gelöscht',
  category_created: 'Kategorie erstellt', category_updated: 'Kategorie geändert', category_deleted: 'Kategorie gelöscht',
  project_created: 'Projekt-ID-Feld erstellt', project_deleted: 'Projekt-ID-Feld gelöscht', news_deleted: 'News gelöscht (Moderation)',
}[a] || a);

export function AvatarWithCard({ userId, image, size = 24, API_URL, authHeaders, avatarClass = '', placeholderClass = '' }) {
  const [open, setOpen] = useState(false);
  const [card, setCard] = useState(null);

  const load = () => {
    if (card || !userId) return;
    axios.get(`${API_URL}/api/community/users/${userId}/card`, { headers: authHeaders })
      .then(r => setCard(r.data))
      .catch(() => setCard({ bio: '', profile_image: '' }));
  };

  const largeImg = card?.profile_image ? `${API_URL}${card.profile_image}` : image;

  return (
    <span
      className="avatar-card-wrap"
      onMouseEnter={() => { setOpen(true); load(); }}
      onMouseLeave={() => setOpen(false)}
      onClick={e => { e.stopPropagation(); if (!open) { setOpen(true); load(); } }}
      data-testid={`avatar-card-${userId}`}
    >
      {image ? <img src={image} alt="" className={avatarClass} /> : <UserCircle size={size} className={placeholderClass} />}
      {open && (
        <span className="avatar-hover-card" data-testid="avatar-hover-card">
          {largeImg ? <img src={largeImg} alt="" className="avatar-hover-img" /> : <UserCircle size={90} className="avatar-hover-placeholder" />}
          {typeof card?.hearts === 'number' && <span className="avatar-hover-hearts" data-testid="avatar-hover-hearts">❤️ {card.hearts}</span>}
          <span className="avatar-hover-bio" data-testid="avatar-hover-bio">{card === null ? 'Lädt...' : (card.bio || 'Keine Biographie hinterlegt.')}</span>
        </span>
      )}
    </span>
  );
}
